import React from 'react'

import Box from 'app/components/base/Box/Box'
import Stack from 'app/components/base/Stack'
import ShareButton from 'app/components/buttons/ShareButton'
import QuantityController from 'app/features/ListingFeature/QuantityController'
import useAppStore from 'app/store/app.store'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'

import AddToWishlistButton from './components/AddToWishlistButton'
import ListingContainer from './components/containers/ListingContainer'
import ListingInfoContainer from './components/containers/ListingInfoContainer'
import SellerHolidayHint from './components/SellerHolidayHint'
import ContactSeller from './ContactSeller'
import { useShowListingFeature } from './hooks/useShowListingFeature'
import ImageSlider from './ImageSlider'
import ListingInformation from './ListingInformation'
import ShowActions from './ShowActions'
import SimilarItems from './SimilarItems'
import TitleInfo from './TitleInfo'
import YouRecentlyViewed from './YouRecentlyViewed'

interface ShowListingProps {
  listing?: ListingModel
  slug: string
  isMobile: boolean
  isLoading?: boolean
  isLargeSize?: boolean
}

const ShowListingFeature: RFC<ShowListingProps> = ({
  listing,
  slug,
  isMobile,
  isLoading,
  isLargeSize
}) => {
  const isTablet = useAppStore((state) => state.isTablet)

  const {
    categoryBreadUrl,
    isHolidayMode,
    subCategoryBreadUrl,
    seller,
    sellerAvatar,
    isAuthUserListing,
    isSold,
    isAuction,
    isReserved,
    isDisabled,
    isAuctionSold,
    acceptOffers,
    url,
    media,
    availableQuantity,
    sellerPaymentGateways,
    viewedItems,
    similarItems,
    isPersonalSeller
  } = useShowListingFeature({
    isLoading,
    listing,
    slug
  })

  const breadItems = [
    {
      child: listing?.sub_category.parent?.name,
      href: categoryBreadUrl
    },
    {
      child: listing?.sub_category.name,
      href: subCategoryBreadUrl
    }
  ]

  const hasSimilarItems = !!similarItems && similarItems?.length > 0
  const showDesktopSimilarItems = isSold && !isMobile && hasSimilarItems
  const showMobileSimilarItems = isSold && isMobile && hasSimilarItems
  const sellerName = seller?.store_name || seller?.contact_name

  return (
    <Stack
      sx={{
        py: { md: isTablet ? 0 : 6, xs: 0 },
        alignItems: 'center',
        position: 'relative'
      }}
    >
      <SellerHolidayHint
        visible={isHolidayMode}
        sx={{
          ...(IS_NATIVE_APP && { opacity: 0, height: 0 }),
          ...(!IS_NATIVE_APP && {
            display: { xs: 'none', md: 'flex' },
            maxWidth: 812
          })
        }}
      />

      <ListingContainer breadItems={breadItems}>
        <Stack
          spacing={isMobile ? 0 : 2}
          sx={{ pr: { md: isTablet ? 0 : 4.75, xs: 0 } }}
        >
          <ImageSlider
            isMobile={isMobile}
            images={listing?.attachments || []}
            listingId={String(listing?.id)}
            isLiked={listing?.liked}
            listingUserId={listing?.user?.id}
            loading={isLoading}
          />

          <Box
            sx={{
              ...(IS_NATIVE_APP && { opacity: 0, height: 0 }),
              ...(!IS_NATIVE_APP && { display: { xs: 'none', md: 'flex' } })
            }}
          >
            <ContactSeller
              name={sellerName}
              avatar={sellerAvatar}
              role={seller?.role}
              rating={seller?.rating}
              chatId={listing?.id}
              sellerSlug={seller?.slug}
              isOwner={isAuthUserListing}
            />
          </Box>
        </Stack>

        <ListingInfoContainer>
          <TitleInfo
            avatar={sellerAvatar}
            auctionEndDate={listing?.auction?.end_date}
            condition={listing?.condition || ''}
            itemType={listing?.item_type || ''}
            subTitle={listing?.subtitle || ''}
            title={listing?.title}
            warranty={listing?.warranty_valid_until}
            shipping={listing?.shipping || ({} as ShippingModel)}
            internationalShipping={listing?.shipping.international_shipping}
            isSold={isSold}
            isReserved={isReserved}
            isHolidayMode={isHolidayMode}
            loading={isLoading}
            isPersonalSeller={isPersonalSeller}
            sellerSlug={seller?.slug}
          />

          {!IS_NATIVE_APP && (
            <ShowActions
              actualPrice={listing?.actual_price}
              convertedActualPrice={listing?.converted_actual_price}
              auctionId={listing?.auction?.id}
              bidsCount={listing?.auction?.bids_count}
              auctionReserveStatus={listing?.auction?.reserve_status}
              disabled={isDisabled}
              acceptOffers={acceptOffers}
              isAuthUserListing={isAuthUserListing}
              listingCurrency={listing?.currency}
              slug={listing?.slug || ''}
              inWatchList={listing?.auction?.in_watchlist}
              watchListId={listing?.auction?.watchlist_id}
              exchangeRate={listing?.exchange_rate}
              availableQuantity={listing?.available_quantity}
              soldQuantity={listing?.sold_quantity}
              reservedQuantity={listing?.reserved_offer_quantity}
              listingId={listing?.id}
              isAuction={isAuction}
              hideQuantity={isAuction || isAuctionSold}
              isSold={isSold}
              isHighestBidder={listing?.highest_bidder}
              isOutbid={listing?.outbid}
              maxBid={listing?.max_bid}
              convertedTradePrice={listing?.converted_trade_price}
              tradePrice={listing?.trade_price}
              showSimilarItems={showDesktopSimilarItems}
              similarItems={similarItems}
              isLargeSize={isLargeSize}
              listingTitle={listing?.title}
            />
          )}

          {showMobileSimilarItems && (
            <SimilarItems similarItems={similarItems} isMobile />
          )}

          <QuantityController
            availableQuantity={availableQuantity}
            soldQuantity={listing?.sold_quantity}
            reservedQuantity={listing?.reserved_offer_quantity}
            hideQuantity={isAuction || isAuctionSold}
            isOffer={acceptOffers}
            isSold={isSold}
            sx={{
              ...(IS_NATIVE_APP && {
                ...(showMobileSimilarItems && { opacity: 0, height: 0 })
              }),
              ...(!IS_NATIVE_APP && {
                display: {
                  xs: showMobileSimilarItems ? 'none' : 'flex',
                  md: 'none'
                }
              })
            }}
          />

          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <ShareButton
              url={url}
              media={media}
              listingTitle={listing?.title || ''}
            />

            <AddToWishlistButton
              listingId={String(listing?.id)}
              isLiked={listing?.liked}
              listingUserId={listing?.user?.id}
            />
          </Stack>

          <ListingInformation
            loading={isLoading}
            listing={listing || ({} as ListingModel)}
            isAuthUserListing={isAuthUserListing}
            paymentGateways={sellerPaymentGateways}
          />

          <Box
            sx={{
              ...(!IS_NATIVE_APP && { display: { xs: 'flex', md: 'none' } })
            }}
          >
            <ContactSeller
              name={sellerName}
              avatar={sellerAvatar}
              role={seller?.role}
              rating={seller?.rating}
              chatId={listing?.id}
              sellerSlug={seller?.slug}
              isOwner={isAuthUserListing}
            />
          </Box>
        </ListingInfoContainer>
      </ListingContainer>

      <YouRecentlyViewed
        slugs={viewedItems}
        isMobile={isMobile}
        loading={isLoading}
        isLargeSize={isLargeSize}
      />
    </Stack>
  )
}

export default ShowListingFeature
